import { ChunkStatus } from './chunk-status';
import { createResponse } from './create-response';
import { fileTypes } from './file-types';

const CHUNK_INIT_PATH = '/files/upload';
const CHUNK_UPLOAD_PATH = '/files/upload/chunk';

export const chunkedUpload = function (fieldName, file, metadata, load, error, progress, abort, transfer, options, csrfToken) {
    // all chunks
    const chunks = [];
    const { chunkTransferId, chunkServer, chunkSize, chunkRetryDelays } = options;

    // default state
    let state = {
        serverId: chunkTransferId,
        aborted: false,
        fileId: 0,
        renamedFilename: null
    };

    //get the total amount of procecessed bytes
    //used to offset next chunk size starting point
    const totalProcessedBytes = (chunks, currentIndx) => {
        let size = -1;

        for (let i = 0; i < chunks.length; i++) {
            if (i == currentIndx) { break;
            }
            size += chunks[i].size;
        }

        return size;
    };

    // const totalBytesTransfered = chunks.reduce((p, chunk) => {
    //     if (p === null || chunk.progress === null) return null;
    //     return p + chunk.progress;
    // }, 0);

    // create chunks
    const lastChunkIndex = Math.floor(file.size / chunkSize);

    for (let i = 0; i <= lastChunkIndex; i++) {
        const offset = i * chunkSize;
        const data = file.slice(offset, offset + chunkSize, 'application/offset+octet-stream');
        chunks[i] = {
            index: i,
            size: data.size,
            offset,
            data,
            file,
            progress: 0,
            retries: [...chunkRetryDelays],
            status: ChunkStatus.QUEUED,
            error: null,
            request: null,
            timeout: null,
            abortController: null,
        }
    }

    const completeProcessingChunks = () => {
        load(state.fileId || state.serverId)

        //add file to queue of files that we will rename
        //when we have a completed file object
        if (state.renamedFilename) {

            //@TODO - Is there a way to add this to VueX store and use outside of VueX component??
            this.addToFileRenameQueue(file, { renamedFilename: state.renamedFilename });
        }
    };

    const canProcessChunk = chunk => chunk.status === ChunkStatus.QUEUED || chunk.status === ChunkStatus.ERROR;

    const calcTotalChunkSize = () => chunks.reduce((total, chunk) => total + chunk.size, 0);

    //initialize
    const initializeChunkProcessing = () => {
        const abortController = new AbortController();

        const headers = {
            'Upload-Length': calcTotalChunkSize(),
            'Upload-Name': file.name,
            'Upload-Offset': 0,
            'Content-Type': 'application/offset+octet-stream',
        };

        return axios(
            {
                url: CHUNK_INIT_PATH,
                method: 'POST',
                signal: abortController.signal,
                headers
            }
        )
    .then(result => result.data)
    };

    //upload a chunk
    const processChunk = (chunk) => {
        // processing is paused, wait here
        if (state.aborted) { return;
        }

        // get next chunk to process
        chunk = chunk || chunks.find(canProcessChunk);

        // no more chunks to process
        if (!chunk) {
            // all done?
            if (chunks.every(chunk => chunk.status === ChunkStatus.COMPLETE)) { completeProcessingChunks();
            }

            // no chunk to handle
            return;
        };

        // now processing this chunk
        chunk.status = ChunkStatus.PROCESSING;
        chunk.progress = null;

        // allow parsing of formdata
        const onData = chunkServer.ondata || (fd => fd);
        const onError = chunkServer.onerror || (res => null);

        const abortController = new AbortController();
        let startByte = totalProcessedBytes(chunks, chunk.index);
        const totalSize = calcTotalChunkSize();

        //get the uploaded file information/form data
        const formData = (chunk) => {
            const formData = new FormData();
            // fieldName is the name of the input field
            // file is the actual file object to send
            formData.append('orderfiletype', options.orderFileType || fileTypes['source']);
            formData.append('patch', state.serverId);
            formData.append('_method', 'PATCH');
            formData.append('orderid', options.orderId || 0); //this.orderId);
            formData.append('files[]', chunk.data, chunk.file.name);

            return formData;
        }

        const endByte = startByte + chunk.size;

        //start on the next byte
        if (startByte > 0 ) { startByte += 1;
        }

        let headers = {
            'Content-Type': 'multipart/form-data;',
            'X-CSRF-TOKEN' : csrfToken,
            //'Content-Length': chunk.size,
            'Content-Disposition': `attachment; filename="${file.name}"`,
            //Content-Range: bytes 0-19999999/104857600
            'Content-Range': `bytes ${Math.max(0, startByte)}-${endByte}/${totalSize}`,
            'Upload-Length': totalSize,
            'Upload-Name': file.name,
            'Upload-Offset': chunk.offset,
            'Content-Type': 'application/offset+octet-stream',
            //'Content-Type' : 'multipart/form-data;',
        };

        chunk.abortController = abortController;

        //upload the chunk
        return chunk.request = axios(
            {
                url: CHUNK_UPLOAD_PATH,
                method: 'POST',
                data: onData(formData(chunk)),
                signal: abortController.signal,
                headers,
                onUploadProgress: function (e) {
                    chunk.progress = e.lengthComputable ? e.loaded : null;
                    updateTotalProgress();
                }
            }
        )
    .then(
        (result) => {
        let { files } = result.data;
        // done!
            chunk.status = ChunkStatus.COMPLETE;
        // remove request reference
            chunk.request = null;
        //if this is the last chunk let's store the file id
            if (files) {
                let file = files[0];

                if (file.id) {
                    state.fileId = file.id;

                    //add to files list
                    this.files = this.files.concat(files)

                    state.renamedFilename = file.renamedFilename;
                }
            }

            // start processing more chunks
            processChunks();
        }
    )        .catch(
        (err, res) => {
        //request aborted
            if (err.name === 'AbortError') {
                chunk.status = ChunkStatus.QUEUED;
                chunk.request = null;
                abort();
            } else {
                // handle error
                chunk.status = ChunkStatus.ERROR;
                chunk.request = null;
                chunk.error = onError(err.response) || err.message;

                //try again?
                if (!retryProcessChunk(chunk)) {
                    error(
                        createResponse(
                            'error',
                            err.response.status,
                            onError(err.response) || err.message,
                            err.response.headers
                        )
                    );
                }
            }
        }
    )
    };

    const retryProcessChunk = (chunk) => {
        // no more retries left
        if (chunk.retries.length === 0) { return false;
        }

        // new retry
        chunk.status = ChunkStatus.WAITING;
        clearTimeout(chunk.timeout);
        chunk.timeout = setTimeout(
            () => {
            processChunk(chunk);
            }, chunk.retries.shift()
        );

    // we're going to retry
    return true;
    };

    const updateTotalProgress = () => {

        // calculate total progress fraction
        const totalBytesTransfered = chunks.reduce(
            (p, chunk) => {
            if (p === null || chunk.progress === null) { return null;
            }
            return p + chunk.progress;
            }, 0
        );

    // can't compute progress
    if (totalBytesTransfered === null) { return progress(false, 0, 0);
    }

    // calculate progress values
    const totalSize = chunks.reduce((total, chunk) => total + chunk.size, 0);

    EventBus.$emit(
        'filepond-progress', {
            totalBytesTransfered,
            totalSize
        }
    )

    // can update progress indicator
    progress(true, totalBytesTransfered, totalSize)
    };

    // process new chunks
    const processChunks = () => {
        const totalProcessing = chunks.filter(chunk => chunk.status === ChunkStatus.PROCESSING).length;
        if (totalProcessing >= 1) { return;
        }
        processChunk();
    };

    //cancel all chunks
    //@TODO - when we cancel/abort you should call the delete file method
    //to clean up any lingering files or DB entries
    //this should probably be implemented on the backend if not already
    const abortChunks = () => {
        chunks.forEach(
            chunk => {
            clearTimeout(chunk.timeout);
            if (chunk.request) {
                chunk.abortController.abort()
            }
            }
        )
    };

    // start process
    initializeChunkProcessing()
    .then(
        filepondId => {
        // pass back to item so we can use it if something goes wrong
            transfer(filepondId);
        // store internally
            state.serverId = filepondId;
        processChunks();
        }
    )
    .catch(
        (err, res) => {
        console.error('initialize chunk error:', err);
        //if the initialization failed then abort everything
            abort();
        }
    )

    return {
        abort: () => {
            state.aborted = true;

            //if we already initialized the chunks
            if (state.serverId) {
                abortChunks();
            }
        }
    }
};
